import type { ReactNode } from 'react';
import React, { useEffect } from 'react';
import clsx from 'clsx';
import { gql } from '@soundxyz/gql-string';
import { fadeInAnimation } from '@components/animations/fade';
import { Skeleton } from '@components/main-components/skeleton/Skeleton';
import { useQuery } from '@graphql/reactQuery';
import { TotalRaisedDocument } from '@graphql/reactQuery';
import { useLogError } from '@hooks/useLogError';
import { CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY } from '@utils/constants';
import { formatUSD } from '@utils/helpers';

/*#__PURE__*/ gql(/* GraphQL */ `
  query totalRaised {
    totalRaisedPlatform {
      usd
    }
  }
`);

export const fadeIn = fadeInAnimation({ duration: 0.15, delay: 0.25 });

const AnimatedText = ({
  isFirstLine = false,
  children,
}: {
  isFirstLine?: boolean;
  children: ReactNode;
}) => (
  <p
    className={clsx(
      'absolute left-0 flex w-full animate-[animatedTextScale_4.5s_ease-in-out_infinite] items-center justify-center text-base-m font-medium [transform-origin:center_left] md:justify-start',
      isFirstLine
        ? 'top-[-18px] [animation-direction:alternate-reverse]'
        : 'top-0 [animation-direction:alternate]',
    )}
  >
    {children}
  </p>
);

export const HeaderAnimatedText = () => {
  const logError = useLogError();
  const {
    data,
    isLoading: loading,
    error: getTotalRaisedError,
  } = useQuery(TotalRaisedDocument, {
    ...CLIENT_QUERY_REDUCTION_ON_FRESH_SSR_STRATEGY,
  });

  useEffect(() => {
    if (getTotalRaisedError) {
      logError({ error: getTotalRaisedError });
    }
  }, [logError, getTotalRaisedError]);

  return (
    <div className="mt-2 h-[18px] overflow-hidden font-medium text-white/70 md:mt-4">
      <div className="relative h-full animate-[animatedTextRotation_4.5s_ease-in-out_infinite_alternate]">
        <AnimatedText isFirstLine>
          <span>
            <span className="text-white">
              {loading || !data ? (
                // Sized based on the 7 figure value that is in production
                // Needs to be a span to prevent invalid HTML nesting during SSR
                <Skeleton
                  as="span"
                  className="relative top-px mr-[2px] inline-block h-[13px] w-[76px] rounded-md"
                />
              ) : (
                formatUSD(data?.data.totalRaisedPlatform?.usd || 0, true)
              )}
            </span>
            &nbsp;Earned by artists
          </span>
          <span>&nbsp;from fans on Sound</span>
        </AnimatedText>
        <AnimatedText>
          <span>
            Discover <span className="text-white">new music </span>and&nbsp;
          </span>
          <span>
            prove you were there <span className="text-white">first</span>
          </span>
        </AnimatedText>
      </div>
    </div>
  );
};
